import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createFileReviewMessage,
  createFileReviewPdfAnchor,
  createFileReviewSpreadsheetAnchor,
  createFileReviewWordAnchor,
  deleteFileReviewMessage,
  fetchFileReviewThread,
  updateFileReviewMessage
} from './api/fileReviewApi';
import { discoverPeerThreadActivity } from './discussionPeerActivity';
import type { DiscussionPeerActivityEvent } from './discussionPeerActivity';
import { createFileReviewDiscussionClient } from './realtime/createFileReviewDiscussionClient';
import type { FileReviewThreadChangedNotice } from './realtime/fileReviewDiscussionTypes';
import type {
  FileReviewAnchorKind,
  FileReviewDocumentKind,
  FileReviewThreadDto,
  PdfHighlightArea,
  SpreadsheetCellRange,
  WordTextRange
} from './types';

const POLL_INTERVAL_MS = 60_000;
// Coalesce bursts (e.g. create-anchor immediately followed by create-message) into one reload.
const REALTIME_RELOAD_DEBOUNCE_MS = 250;

function threadHasSameReviewContent(
  current: FileReviewThreadDto | null,
  next: FileReviewThreadDto
): boolean {
  if (!current) return false;
  if (current.threadId !== next.threadId || current.canAnnotate !== next.canAnnotate) return false;
  if (current.messages.length !== next.messages.length) return false;
  if (current.anchors.length !== next.anchors.length) return false;

  for (let i = 0; i < current.messages.length; i++) {
    const a = current.messages[i];
    const b = next.messages[i];
    if (
      a.id !== b.id ||
      a.updatedAt !== b.updatedAt ||
      a.text !== b.text ||
      a.anchorId !== b.anchorId ||
      a.anchorKind !== b.anchorKind
    ) {
      return false;
    }
  }

  for (let i = 0; i < current.anchors.length; i++) {
    const a = current.anchors[i];
    const b = next.anchors[i];
    if (
      a.id !== b.id ||
      a.kind !== b.kind ||
      a.selectedText !== b.selectedText ||
      a.createdAt !== b.createdAt
    ) {
      return false;
    }
  }

  return true;
}

export interface UseFileReviewThreadOptions {
  commentsPanelOpen?: boolean;
  onError?: (message: string) => void;
  currentUserId?: string;
  currentUserAliases?: Iterable<string>;
  onPeerActivity?: (event: DiscussionPeerActivityEvent) => void;
}

export function useFileReviewThread(
  mountId: string,
  path: string,
  enabled: boolean,
  options?: UseFileReviewThreadOptions
) {
  const commentsPanelOpen = options?.commentsPanelOpen ?? true;
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;
  const onPeerActivityRef = useRef(options?.onPeerActivity);
  onPeerActivityRef.current = options?.onPeerActivity;
  const currentUserIdRef = useRef(options?.currentUserId ?? 'unknown');
  currentUserIdRef.current = options?.currentUserId ?? 'unknown';
  const currentUserAliasesRef = useRef(options?.currentUserAliases ?? []);
  currentUserAliasesRef.current = options?.currentUserAliases ?? [];

  const [thread, setThread] = useState<FileReviewThreadDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasThreadRef = useRef(false);
  const threadRef = useRef<FileReviewThreadDto | null>(null);
  const reloadSeqRef = useRef(0);
  const pendingNoticesRef = useRef<FileReviewThreadChangedNotice[]>([]);

  const notifyError = useCallback((e: unknown) => {
    const message = e instanceof Error ? e.message : String(e);
    onErrorRef.current?.(message);
    return message;
  }, []);

  useEffect(() => {
    hasThreadRef.current = false;
    threadRef.current = null;
    pendingNoticesRef.current = [];
    setThread(null);
    setError(null);
    setLoading(false);
    reloadSeqRef.current += 1;
  }, [mountId, path]);

  const emitPeerActivityIfNeeded = useCallback(
    (previousThread: FileReviewThreadDto | null, nextThread: FileReviewThreadDto, silent: boolean) => {
      if (!silent || !onPeerActivityRef.current) return;

      const notices = pendingNoticesRef.current;
      pendingNoticesRef.current = [];
      const notice = notices.length > 0 ? notices[notices.length - 1]! : null;

      const event = discoverPeerThreadActivity(
        previousThread,
        nextThread,
        notice,
        currentUserIdRef.current,
        currentUserAliasesRef.current
      );
      if (event) onPeerActivityRef.current(event);
    },
    []
  );

  const reload = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!enabled || !mountId || !path) return;

      const silent = opts?.silent ?? false;
      const seq = ++reloadSeqRef.current;

      if (!silent && !hasThreadRef.current) {
        setLoading(true);
      }
      if (!silent) {
        setError(null);
      }

      try {
        const data = await fetchFileReviewThread(mountId, path);
        if (seq !== reloadSeqRef.current) return;
        const previousThread = threadRef.current;
        if (silent && threadHasSameReviewContent(previousThread, data)) {
          pendingNoticesRef.current = [];
          hasThreadRef.current = true;
          setError(null);
          return;
        }
        emitPeerActivityIfNeeded(previousThread, data, silent);
        threadRef.current = data;
        setThread(data);
        hasThreadRef.current = true;
        setError(null);
      } catch (e) {
        if (seq !== reloadSeqRef.current) return;
        const message = notifyError(e);
        if (!silent) {
          setError(message);
          // Keep the last good thread — a stale or rate-limited refresh must not wipe comments.
        }
      } finally {
        if (seq === reloadSeqRef.current && !silent) {
          setLoading(false);
        }
      }
    },
    [emitPeerActivityIfNeeded, enabled, mountId, notifyError, path]
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  // Keep a stable handle to the latest reload so the realtime connection does not churn.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  // Real-time discussion updates: reload (silently) when a peer changes the thread. Polling below
  // stays as a safety net for dropped connections / SignalR outages.
  useEffect(() => {
    if (!enabled || !mountId || !path) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReload = (changedNotice: FileReviewThreadChangedNotice) => {
      pendingNoticesRef.current.push(changedNotice);
      if (debounceTimer != null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void reloadRef.current({ silent: true });
      }, REALTIME_RELOAD_DEBOUNCE_MS);
    };

    const client = createFileReviewDiscussionClient({
      mountId,
      path,
      onThreadChanged: scheduleReload
    });
    void client.start();

    return () => {
      if (debounceTimer != null) clearTimeout(debounceTimer);
      void client.stop();
    };
  }, [enabled, mountId, path]);

  useEffect(() => {
    if (!enabled || !mountId || !path || !commentsPanelOpen) return;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      void reload({ silent: true });
    };

    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void reload({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [commentsPanelOpen, enabled, mountId, path, reload]);

  const commitPdfAnchor = useCallback(
    async (payload: { areas: PdfHighlightArea[]; selectedText: string; comment: string }) => {
      if (!thread?.canAnnotate) {
        notifyError('Comments are read-only for this file.');
        return undefined;
      }
      if (!payload.areas.length) {
        notifyError('Could not link comment to the selection. Select content and try again.');
        return undefined;
      }
      try {
        const pageIndex = payload.areas[0]?.pageIndex ?? 0;
        const { id: anchorId } = await createFileReviewPdfAnchor(mountId, {
          path,
          pageIndex,
          areas: payload.areas,
          selectedText: payload.selectedText
        });
        if (payload.comment.trim()) {
          await createFileReviewMessage(mountId, {
            path,
            text: payload.comment.trim(),
            anchorId,
            anchorKind: 'pdf'
          });
        }
        await reload();
        return anchorId;
      } catch (e) {
        notifyError(e);
        return undefined;
      }
    },
    [mountId, notifyError, path, reload, thread?.canAnnotate]
  );

  const commitSpreadsheetAnchor = useCallback(
    async (payload: {
      sheetIndex: number;
      sheetName: string;
      ranges: SpreadsheetCellRange[];
      selectedText: string;
      comment: string;
    }) => {
      if (!thread?.canAnnotate) {
        notifyError('Comments are read-only for this file.');
        return undefined;
      }
      if (!payload.ranges.length) {
        notifyError('Select one or more cells before adding a comment.');
        return undefined;
      }
      try {
        const { id: anchorId } = await createFileReviewSpreadsheetAnchor(mountId, {
          path,
          sheetIndex: payload.sheetIndex,
          sheetName: payload.sheetName,
          ranges: payload.ranges,
          selectedText: payload.selectedText
        });
        if (payload.comment.trim()) {
          await createFileReviewMessage(mountId, {
            path,
            text: payload.comment.trim(),
            anchorId,
            anchorKind: 'spreadsheet'
          });
        }
        await reload();
        return anchorId;
      } catch (e) {
        notifyError(e);
        return undefined;
      }
    },
    [mountId, notifyError, path, reload, thread?.canAnnotate]
  );

  const commitWordAnchor = useCallback(
    async (payload: {
      range: WordTextRange;
      selectedText: string;
      comment: string;
    }) => {
      if (!thread?.canAnnotate) {
        notifyError('Comments are read-only for this file.');
        return undefined;
      }
      if (!payload.range || payload.range.length <= 0) {
        notifyError('Select document text before adding a comment.');
        return undefined;
      }
      try {
        const { id: anchorId } = await createFileReviewWordAnchor(mountId, {
          path,
          range: payload.range,
          selectedText: payload.selectedText
        });
        if (payload.comment.trim()) {
          await createFileReviewMessage(mountId, {
            path,
            text: payload.comment.trim(),
            anchorId,
            anchorKind: 'word'
          });
        }
        await reload();
        return anchorId;
      } catch (e) {
        notifyError(e);
        return undefined;
      }
    },
    [mountId, notifyError, path, reload, thread?.canAnnotate]
  );

  const sendMessage = useCallback(
    async (
      text: string,
      anchor?: { id: string; kind: FileReviewAnchorKind } | null
    ) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (!thread?.canAnnotate) {
        notifyError('Comments are read-only for this file.');
        return;
      }
      try {
        await createFileReviewMessage(mountId, {
          path,
          text: trimmed,
          anchorId: anchor?.id ?? null,
          anchorKind: anchor?.kind ?? null
        });
        await reload();
      } catch (e) {
        notifyError(e);
        throw e;
      }
    },
    [mountId, notifyError, path, reload, thread?.canAnnotate]
  );

  const updateMessage = useCallback(
    async (messageId: string, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (!thread?.canAnnotate) {
        notifyError('Comments are read-only for this file.');
        return;
      }
      try {
        await updateFileReviewMessage(mountId, messageId, { text: trimmed });
        await reload();
      } catch (e) {
        notifyError(e);
        throw e;
      }
    },
    [mountId, notifyError, reload, thread?.canAnnotate]
  );

  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!thread?.canAnnotate) {
        notifyError('Comments are read-only for this file.');
        return;
      }
      try {
        await deleteFileReviewMessage(mountId, messageId);
        await reload();
      } catch (e) {
        notifyError(e);
        throw e;
      }
    },
    [mountId, notifyError, reload, thread?.canAnnotate]
  );

  const anchorsForKind = useCallback(
    (kind: FileReviewDocumentKind) =>
      thread?.anchors.filter((a: { kind: FileReviewDocumentKind }) => a.kind === kind) ?? [],
    [thread?.anchors]
  );

  return {
    thread,
    loading,
    error,
    reload,
    messages: thread?.messages ?? [],
    anchors: thread?.anchors ?? [],
    anchorsForKind,
    canAnnotate: thread?.canAnnotate ?? false,
    commitPdfAnchor,
    commitSpreadsheetAnchor,
    commitWordAnchor,
    sendMessage,
    updateMessage,
    deleteMessage
  };
}
